import { isMobileViewport } from "../../game/platform";
import type { RenderModel } from "../renderModel";
import {
  isSinglePlayerHudPolishPhase,
  mFont,
  resolveDoomFillCss,
  resolveLegacyDoomRampCss,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface DoomMeterDeps {
  ctx: CanvasRenderingContext2D;
  box: number;
  boardY: number;
  bps: number;
  nowMs: () => number;
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void;
  reducedMotionPreferred: boolean;
  gameplayDoomDangerActive: boolean;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawDoomMeterScreen(deps: DoomMeterDeps, model: RenderModel): void {
  const { ctx, box, boardY, bps, nowMs, drawHourglass, reducedMotionPreferred, gameplayDoomDangerActive } = deps;
  const rowRushTimerMode = model.soloMode === "row-rush";

  const doomFillFrac = Math.max(
    0,
    Math.min(1, model.doomClockMaxSec > 0 ? model.doomClockSec / model.doomClockMaxSec : 0)
  );
  const singlePlayerHudPolish = isSinglePlayerHudPolishPhase(model.phase);
  const doomDangerActive = singlePlayerHudPolish
    ? gameplayDoomDangerActive
    : false;
  const doomFrozen = model.doomClockFrozen;
  const doomWarning = singlePlayerHudPolish
    ? doomDangerActive
    : model.warningActive || doomFillFrac <= 0.25;

  // Mobile: horizontal doom bar below the board
  if (isMobileViewport()) {
    drawDoomMeterHorizontal(
      ctx, doomFillFrac, doomFrozen, doomWarning, singlePlayerHudPolish,
      box, boardY, bps, nowMs, drawHourglass, reducedMotionPreferred, rowRushTimerMode
    );
    return;
  }

  // Desktop: vertical layout — skull box at bottom, bar segments above (left of board, filling margin)
  const doomMargin = 4;
  const doomW = box - doomMargin * 2; // fill left margin (e.g. 38 - 8 = 30px)
  const doomX = doomMargin;
  const skullBoxH = 30;
  const skullBoxY = boardY + bps - skullBoxH;
  const barY = boardY;
  const barH = bps - skullBoxH - 4;

  // --- Skull box (bottom) ---
  roundedRectPath(ctx, doomX, skullBoxY, doomW, skullBoxH, 6);
  ctx.fillStyle = "rgb(20,28,50)";
  ctx.fill();
  roundedRectPath(ctx, doomX, skullBoxY, doomW, skullBoxH, 6);
  ctx.strokeStyle = doomWarning ? "rgb(255,120,120)" : "rgb(80,100,140)";
  ctx.lineWidth = 1.2;
  ctx.stroke();

  const skCX = doomX + doomW / 2;
  const skCY = skullBoxY + skullBoxH / 2;
  if (rowRushTimerMode) {
    drawHourglass(skCX, skCY, doomW * 0.7, "rgba(220,236,255,0.95)");
  } else {
    // Skull icon centered in box (scaled to bar width)
    const skullPulse = 0.5 + 0.5 * Math.sin(nowMs() / 140);
    const skS = doomW / 24; // scale factor — smaller skull within box
    ctx.save();
    ctx.translate(skCX, skCY);
    ctx.scale(skS, skS);
    ctx.fillStyle = `rgba(200,210,230,${(0.65 + skullPulse * 0.25).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(0, -3, 6.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(-5.5, -2.5, 11, 7.5);
    ctx.fillStyle = "#0b1020";
    ctx.beginPath();
    ctx.arc(-2.4, -3.5, 1.5, 0, Math.PI * 2);
    ctx.arc(2.4, -3.5, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(0, -1.8);
    ctx.lineTo(-1.3, 0);
    ctx.lineTo(1.3, 0);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(10,16,28,0.9)";
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(-3.2, 2.2);
    ctx.lineTo(3.2, 2.2);
    ctx.moveTo(-1.1, 2.2);
    ctx.lineTo(-1.1, 4);
    ctx.moveTo(1.1, 2.2);
    ctx.lineTo(1.1, 4);
    ctx.stroke();
    ctx.restore();
  }

  // --- Bar segments (above skull box) ---
  roundedRectPath(ctx, doomX, barY, doomW, barH, 6);
  ctx.fillStyle = "rgb(20,28,50)";
  ctx.fill();
  roundedRectPath(ctx, doomX, barY, doomW, barH, 6);
  if (doomFrozen) {
    const pulse = 0.5 + 0.5 * Math.sin(nowMs() / 95);
    ctx.strokeStyle = `rgba(255,255,255,${(0.55 + pulse * 0.45).toFixed(3)})`;
  } else if (doomWarning) {
    if (singlePlayerHudPolish) {
      const pulse = reducedMotionPreferred
        ? 0.76
        : 0.58 + 0.2 * (0.5 + 0.5 * Math.sin(nowMs() / 220));
      ctx.strokeStyle = `rgba(255,112,112,${pulse.toFixed(3)})`;
    } else {
      const pulse = 0.5 + 0.5 * Math.sin(nowMs() / 85);
      ctx.strokeStyle = `rgba(255,120,120,${(0.55 + pulse * 0.45).toFixed(3)})`;
    }
  } else {
    ctx.strokeStyle = "rgb(80,100,140)";
  }
  ctx.lineWidth = 1.2;
  ctx.stroke();

  const blockH = 8;
  const blockGap = 3;
  const innerPad = 4;
  const innerX = doomX + innerPad;
  const innerW = doomW - innerPad * 2;
  const innerTop = barY + 4;
  const innerBot = barY + barH - 4;
  const totalInner = innerBot - innerTop;
  const totalBlocks = Math.floor(totalInner / (blockH + blockGap));
  const filledBlocks = Math.round(totalBlocks * doomFillFrac);
  const doomFillColor = resolveDoomFillCss(doomFillFrac);

  for (let b = 0; b < totalBlocks; b++) {
    const by = innerBot - (b + 1) * (blockH + blockGap) + blockGap;
    const isFilled = b < filledBlocks;

    if (isFilled) {
      ctx.fillStyle = singlePlayerHudPolish
        ? doomFillColor
        : resolveLegacyDoomRampCss(b / Math.max(1, totalBlocks - 1));
    } else {
      ctx.fillStyle = "rgba(30,45,70,0.25)";
    }
    roundedRectPath(ctx, innerX, by, innerW, blockH, 2);
    ctx.fill();
    if (singlePlayerHudPolish) {
      ctx.strokeStyle = "rgba(10,18,30,0.55)";
      ctx.lineWidth = 0.8;
      roundedRectPath(ctx, innerX, by, innerW, blockH, 2);
      ctx.stroke();
    }
  }
  if (singlePlayerHudPolish) {
    drawDoomMajorTicks(ctx, innerX, innerTop, innerW, innerBot - innerTop, "vertical", 8);
    ctx.lineWidth = 1;
  }

  if (doomFrozen) {
    ctx.fillStyle = "rgba(240,246,255,0.16)";
    ctx.fillRect(doomX, barY, doomW, barH);
  }

  // Meter label below icon box
  ctx.fillStyle = "#8899bb";
  ctx.font = mFont("bold", 6);
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(rowRushTimerMode ? "TIME" : "DOOM", doomX + doomW / 2, skullBoxY + skullBoxH + 3);
  if (singlePlayerHudPolish && doomWarning && !doomFrozen) {
    const flash = reducedMotionPreferred
      ? 0.78
      : 0.45 + 0.4 * (0.5 + 0.5 * Math.sin(nowMs() / 260));
    ctx.fillStyle = `rgba(255,132,132,${flash.toFixed(3)})`;
    ctx.font = mFont("bold", 4);
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText("DANGER", doomX + doomW / 2, barY - 2);
  }
}

// ── Private helpers ─────────────────────────────────────────────────

function drawDoomMajorTicks(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  orientation: "vertical" | "horizontal",
  tickCount: number
): void {
  const ticks = Math.max(2, Math.floor(tickCount));
  ctx.save();
  ctx.strokeStyle = "rgba(188,208,240,0.34)";
  ctx.lineWidth = 1;
  for (let i = 1; i < ticks; i += 1) {
    const t = i / ticks;
    if (orientation === "vertical") {
      const ty = y + h - h * t;
      ctx.beginPath();
      ctx.moveTo(x + 1, ty + 0.5);
      ctx.lineTo(x + w - 1, ty + 0.5);
      ctx.stroke();
    } else {
      const tx = x + w * t;
      ctx.beginPath();
      ctx.moveTo(tx + 0.5, y + 1);
      ctx.lineTo(tx + 0.5, y + h - 1);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawDoomMeterHorizontal(
  ctx: CanvasRenderingContext2D,
  doomFillFrac: number,
  doomFrozen: boolean,
  doomWarning: boolean,
  singlePlayerHudPolish: boolean,
  box: number,
  boardY: number,
  bps: number,
  nowMs: () => number,
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void,
  reducedMotionPreferred: boolean,
  rowRushTimerMode: boolean
): void {
  const barH = 26;
  const skullBoxW = 26;
  const skullBoxH = 26;
  const barX = box + skullBoxW + 4;
  const barW = bps - skullBoxW - 4;
  const barY = boardY + bps + 6;
  const skullBoxX = box;
  const skullBoxY = barY;

  // Skull box (left of bar)
  roundedRectPath(ctx, skullBoxX, skullBoxY, skullBoxW, skullBoxH, 5);
  ctx.fillStyle = "rgb(20,28,50)";
  ctx.fill();
  roundedRectPath(ctx, skullBoxX, skullBoxY, skullBoxW, skullBoxH, 5);
  ctx.strokeStyle = doomWarning ? "rgb(255,120,120)" : "rgb(80,100,140)";
  ctx.lineWidth = 1.2;
  ctx.stroke();

  const skCX = skullBoxX + skullBoxW / 2;
  const skCY = skullBoxY + skullBoxH / 2;
  if (rowRushTimerMode) {
    drawHourglass(skCX, skCY, skullBoxW * 0.7, "rgba(220,236,255,0.95)");
  } else {
    // Skull icon
    const skullPulse = 0.5 + 0.5 * Math.sin(nowMs() / 140);
    ctx.save();
    ctx.translate(skCX, skCY);
    ctx.fillStyle = `rgba(200,210,230,${(0.65 + skullPulse * 0.25).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(0, -2.5, 5.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(-4.5, -2, 9, 6);
    ctx.fillStyle = "#0b1020";
    ctx.beginPath();
    ctx.arc(-2, -3, 1.3, 0, Math.PI * 2);
    ctx.arc(2, -3, 1.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(0, -1.2);
    ctx.lineTo(-1, 0.4);
    ctx.lineTo(1, 0.4);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(10,16,28,0.9)";
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(-2.8, 1.8);
    ctx.lineTo(2.8, 1.8);
    ctx.moveTo(-0.9, 1.8);
    ctx.lineTo(-0.9, 3.2);
    ctx.moveTo(0.9, 1.8);
    ctx.lineTo(0.9, 3.2);
    ctx.stroke();
    ctx.restore();
  }

  // Bar background
  roundedRectPath(ctx, barX, barY, barW, barH, 5);
  ctx.fillStyle = "rgb(20,28,50)";
  ctx.fill();
  roundedRectPath(ctx, barX, barY, barW, barH, 5);
  if (doomFrozen) {
    const pulse = 0.5 + 0.5 * Math.sin(nowMs() / 95);
    ctx.strokeStyle = `rgba(255,255,255,${(0.55 + pulse * 0.45).toFixed(3)})`;
  } else if (doomWarning) {
    if (singlePlayerHudPolish) {
      const pulse = reducedMotionPreferred
        ? 0.76
        : 0.58 + 0.2 * (0.5 + 0.5 * Math.sin(nowMs() / 220));
      ctx.strokeStyle = `rgba(255,112,112,${pulse.toFixed(3)})`;
    } else {
      const pulse = 0.5 + 0.5 * Math.sin(nowMs() / 85);
      ctx.strokeStyle = `rgba(255,120,120,${(0.55 + pulse * 0.45).toFixed(3)})`;
    }
  } else {
    ctx.strokeStyle = "rgb(80,100,140)";
  }
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Horizontal block segments (left to right)
  const blockW = 10;
  const blockGap = 3;
  const innerX = barX + 4;
  const innerY = barY + 4;
  const innerH = barH - 8;
  const innerRight = barX + barW - 4;
  const totalInner = innerRight - innerX;
  const totalBlocks = Math.floor(totalInner / (blockW + blockGap));
  const filledBlocks = Math.round(totalBlocks * doomFillFrac);
  const doomFillColor = resolveDoomFillCss(doomFillFrac);

  for (let b = 0; b < totalBlocks; b++) {
    const bx = innerX + b * (blockW + blockGap);
    const isFilled = b < filledBlocks;

    if (isFilled) {
      ctx.fillStyle = singlePlayerHudPolish
        ? doomFillColor
        : resolveLegacyDoomRampCss(b / Math.max(1, totalBlocks - 1));
    } else {
      ctx.fillStyle = "rgba(30,45,70,0.25)";
    }
    roundedRectPath(ctx, bx, innerY, blockW, innerH, 3);
    ctx.fill();
    if (singlePlayerHudPolish) {
      ctx.strokeStyle = "rgba(10,18,30,0.55)";
      ctx.lineWidth = 0.8;
      roundedRectPath(ctx, bx, innerY, blockW, innerH, 3);
      ctx.stroke();
    }
  }

  if (singlePlayerHudPolish) {
    const ticks = 8;
    ctx.save();
    ctx.strokeStyle = "rgba(188,208,240,0.34)";
    ctx.lineWidth = 1;
    for (let i = 1; i < ticks; i += 1) {
      const tx = innerX + (totalInner * i) / ticks;
      ctx.beginPath();
      ctx.moveTo(tx + 0.5, innerY + 1);
      ctx.lineTo(tx + 0.5, innerY + innerH - 1);
      ctx.stroke();
    }
    ctx.restore();
    ctx.lineWidth = 1;
  }

  if (doomFrozen) {
    ctx.fillStyle = "rgba(240,246,255,0.16)";
    ctx.fillRect(barX, barY, barW, barH);
  }

  // Meter label right of bar
  ctx.fillStyle = "#8899bb";
  ctx.font = mFont("bold", 5);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(rowRushTimerMode ? "TIME" : "DOOM", barX + barW + 4, barY + barH / 2);
  if (singlePlayerHudPolish && doomWarning && !doomFrozen) {
    const flash = reducedMotionPreferred
      ? 0.78
      : 0.45 + 0.4 * (0.5 + 0.5 * Math.sin(nowMs() / 260));
    ctx.fillStyle = `rgba(255,132,132,${flash.toFixed(3)})`;
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText("DANGER", barX + barW / 2, barY - 2);
  }
}
